import {
  Stack,
  Table,
  TableCell,
  Typography,
  TableHead,
  TableRow,
  TableBody,
  TableCellProps,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";

import {
  SiteGroupName,
  DosePrescription,
} from "@components/SiteTable/SiteTable.types";

import React from "react";
import SiteGroupDrawerSkeleton from "./SiteGroupDrawerSkeleton";
import { getDoseFractions, getDoses } from "@utils/doseAndFractions";
import { SiteGroupOut } from "@providers/hop-ord-server/api";
export interface SiteGroupDrawerProps {
  index: number;
  siteGroupData?: SiteGroupOut;
  children?: React.ReactNode;
  doseUnit: string;
  fractionUnit: string;
}

const doseMapping = [
  [],
  [],
  ["High:", "Low:"],
  ["High:", "Mid:", "Low:"],
  ["High:", "Mid-1:", "Mid-2:", "Low:"],
];

const siteName = (dosePrescription: DosePrescription): string => {
  return `${dosePrescription.name} (${dosePrescription.laterality}) ${dosePrescription.technique}`;
};

export const siteGroupName = (
  dosePrescriptions: SiteGroupName[],
  doseUnit: string,
  fractionUnit: string,
): string => {
  const siteNames = dosePrescriptions.map((site) => {
    const doses = getDoses(site).join("-");
    if (!site.name) return "";
    const specifyLabel = site.nameSpecify ? ` ${site.nameSpecify}` : "";
    return `${site.name}${specifyLabel} ${site.technique} ${doses}${doseUnit}/${
      site.fractions || ""
    }${fractionUnit}`;
  });
  const siteName = siteNames.join(" + ");
  return siteName;
};

const CustomTableHeaderCell = (props: TableCellProps) => {
  return (
    <TableCell {...props} sx={{ backgroundColor: "action.hover" }}>
      <Typography variant="subtitle2">{props.children}</Typography>
    </TableCell>
  );
};

const CustomTableCell = (props: TableCellProps) => {
  return (
    <TableCell {...props}>
      <Typography variant="body2">{props.children}</Typography>
    </TableCell>
  );
};

const SiteGroupDrawer = (props: SiteGroupDrawerProps) => {
  const { index, siteGroupData, doseUnit, fractionUnit } = props;
  const siteGroupId = (siteGroupData?.id as number) || 0;

  if (!siteGroupData) {
    return <SiteGroupDrawerSkeleton />;
  }

  return (
    <Stack key={siteGroupId} id={`site-group-drawer-${index}`}>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        {siteGroupData?.sites && (
          <Card>
            <CardHeader
              sx={{ paddingBottom: 0 }}
              title={siteGroupName(
                siteGroupData.sites,
                doseUnit || "",
                fractionUnit || "",
              )}
              slotProps={{ title: { variant: "subtitle1" } }}
            />
            <CardContent>
              {siteGroupData?.sites.map((dosePrescription, index) => {
                const doses = getDoses(dosePrescription);
                const doseFractions = getDoseFractions(
                  dosePrescription.fractions || 0,
                  getDoses(dosePrescription),
                );
                const doseLabels = doseMapping[doses.length] || [];
                const joinedDoses = doses
                  .map((dose, i) => `${doseLabels[i] || ""} ${dose}`.trim())
                  .join(", ");
                const joinedFractions = doseFractions
                  .map((fraction, i) =>
                    `${doseLabels[i] || ""} ${fraction}`.trim(),
                  )
                  .join(", ");
                return (
                  <Table key={index} size="small">
                    <TableHead>
                      <TableRow>
                        <CustomTableHeaderCell colSpan={2}>
                          Site {index + 1}: {siteName(dosePrescription)}
                        </CustomTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <CustomTableCell width="40%">Energy</CustomTableCell>
                        <CustomTableCell>
                          {dosePrescription.energy}
                        </CustomTableCell>
                      </TableRow>
                      <TableRow>
                        <CustomTableCell>Dose({doseUnit})</CustomTableCell>
                        <CustomTableCell>{joinedDoses}</CustomTableCell>
                      </TableRow>
                      <TableRow>
                        <CustomTableCell>
                          Fraction({fractionUnit})
                        </CustomTableCell>
                        <CustomTableCell>
                          {dosePrescription.fractions}
                        </CustomTableCell>
                      </TableRow>
                      <TableRow>
                        <CustomTableCell>
                          Dose({doseUnit}/{fractionUnit})
                        </CustomTableCell>
                        <CustomTableCell>{joinedFractions}</CustomTableCell>
                      </TableRow>
                      <TableRow>
                        <CustomTableCell>
                          Prescription PT/Isodose
                        </CustomTableCell>
                        <CustomTableCell>
                          {dosePrescription.isodose}
                        </CustomTableCell>
                      </TableRow>
                      <TableRow>
                        <CustomTableCell>Sequence</CustomTableCell>
                        <CustomTableCell>
                          {dosePrescription.sequence}
                        </CustomTableCell>
                      </TableRow>
                      <TableRow>
                        <CustomTableCell>Frequency</CustomTableCell>
                        <CustomTableCell>
                          {dosePrescription.frequency}
                        </CustomTableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                );
              })}
            </CardContent>
          </Card>
        )}
        {siteGroupData?.organsAtRisk && (
          <Card>
            <CardHeader
              sx={{ paddingBottom: 0 }}
              title="Organ At Risk"
              slotProps={{ title: { variant: "subtitle1" } }}
            />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="50%">Organ</TableCell>
                    <TableCell width="50%">Constraints</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {siteGroupData?.organsAtRisk.map((oar, index) => (
                    <TableRow key={index}>
                      <TableCell data-testid={`oar-organ-${index}`}>
                        {oar.organ}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "pre-wrap",
                        }}
                        data-testid={`oar-constraints-${index}`}
                      >
                        {oar.constraints}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Stack>
  );
};

export default SiteGroupDrawer;
